// How a saved family fact is said back to her. Issue #888 step 3.
//
// Lifted verbatim out of ChatMessages.tsx (where it was a private
// `naturalPhrase`) so voice's spoken/toasted receipt and the chat [LEARN] chip
// read as the same Kin, from one implementation.
//
// ⚠️ Extracted rather than copied ON PURPOSE. This repo already carries several
// hand-mirrored pairs kept in sync by a comment asking the next person to
// remember — the voice SAFETY block, sanitizeFirstName, computeAgeFromBirthdate
// — and #894 exists because four copies of one entitlement gate drifted. A
// receipt that says "Carson's allergic to peanuts" in chat and "Carson —
// allergy: peanuts" in voice is a small drift, but it is the same mechanism.

export interface FamilyFactLike {
  subject: string | null;
  key: string;
  value: string;
}

/**
 * A saved fact, phrased the way a friend would say it back.
 *
 * Falls through to `Subject — key: value` for anything unrecognized, which is
 * plain but never wrong. Inventing a sentence shape for an unknown key risks
 * saying something she did not tell Kin.
 */
/**
 * Facts the chat captures WITHOUT a receipt chip.
 *
 * Kasandra, Oct 20 2026, on seeing "Got it, I'll remember C is a boy":
 * *"it doesn't need to respond and say I'll remember C is a boy.. it should
 * just do it."* So a gender or partner-relation capture is stored and used,
 * and nothing is drawn under the message.
 *
 * ⚠️ The trade-off, stated so nobody rediscovers it: the chip was the only
 * in-chat Undo. A misheard pronoun now surfaces only when Kin uses it, and
 * the correction is Settings → My kids (the toggle reads the same row). That
 * is her call, and it is reversible by removing a key from this set.
 *
 * Keys only — never categories. "kid"/"household" carry allergies, schools
 * and sitters, all of which keep their chip.
 */
export const SILENT_LEARN_KEYS: ReadonlySet<string> = new Set(["gender", "partner_relation"]);

export function isSilentLearnKey(key: string | null | undefined): boolean {
  return SILENT_LEARN_KEYS.has(String(key ?? "").toLowerCase());
}

export function naturalPhrase(fact: FamilyFactLike): string {
  const sub = fact.subject;
  const key = fact.key.toLowerCase();
  const val = fact.value;

  if (sub) {
    if (key === "allergy" || key === "allergies") return `${sub}'s allergic to ${val}`;
    if (key === "birthdate" || key === "birthday") {
      // Parsed and formatted in UTC on both ends. A bare date read in local
      // time lands on the previous day west of Greenwich, so a birthday would
      // render one day early for every user in the US.
      const d = new Date(val + "T00:00:00Z");
      // ⚠️ The `catch` this replaces was decorative. `new Date("not a date")`
      // does NOT throw — it returns an Invalid Date, and toLocaleDateString on
      // one returns the literal string "Invalid Date". So an unparseable value
      // rendered as "Jaxon's birthday is Invalid Date" in the chat chip rather
      // than falling through. Harmless-looking there; voice now SAYS this out
      // loud. Checking the timestamp is the only form that works.
      if (!Number.isNaN(d.getTime())) {
        const formatted = d.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        });
        return `${sub}'s birthday is ${formatted}`;
      }
    }
    if (key === "age") return `${sub} is ${val} years old`;
    if (key === "school") return `${sub} goes to ${val}`;
    if (key === "grade") return `${sub} is in ${val}`;
    // #1862 — "Carson is a boy", not "Carson — gender: boy". The chat chip
    // never shows this key (SILENT_LEARN_KEYS), but voice reads a confirmed
    // fact back aloud and the Undo toast can name it; both go through here,
    // and a database field spoken aloud is the one place the seam would show.
    if (key === "gender") {
      const g = val.trim().toLowerCase();
      if (g === "boy" || g === "son" || g === "male") return `${sub} is a boy`;
      if (g === "girl" || g === "daughter" || g === "female") return `${sub} is a girl`;
    }
    return `${sub} — ${fact.key}: ${val}`;
  }

  if (key === "allergy" || key === "allergies") return `allergic to ${val}`;
  // #1862 — "I'll remember Mike is your husband". No subject on household
  // facts, so this reads the name from the value's sibling row? No — it cannot
  // see it. The relation alone is still a sentence: "your partner is your
  // husband" is what she said, and it is the fact being confirmed.
  if (key === "partner_relation") {
    const r = val.trim().toLowerCase();
    if (r === "husband" || r === "hubby") return `your partner is your husband`;
    if (r === "wife") return `your partner is your wife`;
    if (r === "partner" || r === "spouse") return `you have a partner`;
  }
  if (key === "partner_name") return `your partner is ${val}`;
  return `${fact.key}: ${val}`;
}
